/**
 * Rules for the Deepgram `flux` speech model (PSTUDIO-3630, PSTUDIO-3941).
 *
 * Flux fuses transcription and turn detection, so Inbound Message Buffer no
 * longer applies and stays locked to zero, while End of Turn Threshold applies
 * only to Flux.
 *
 * Max Caller Speaking Time Per Turn was originally locked to zero here on the
 * same reasoning, but that was wrong: it is still what ends a silent caller's
 * turn, and therefore what arms Max Retries / Retry Message. A stored 0 meant
 * the turn never ended and the caller got dead air (AUTO-4962). It is editable
 * on Flux and floored at FLUX_USER_SILENCE_TIMEOUT_MIN (PSTUDIO-3941).
 *
 * Kept as a separate pure module so the conditional behavior is unit-testable
 * without rendering the settings form.
 */

export const FLUX_SPEECH_MODEL = 'flux';

export const isFluxModel = (model?: string | null): boolean =>
  model === FLUX_SPEECH_MODEL;

/** Value Inbound Message Buffer is forced to while Flux is selected. */
export const FLUX_LOCKED_VALUE = 0;

/**
 * Floor for Max Caller Speaking Time Per Turn on Flux. Product decision on
 * PSTUDIO-3941: a silent caller's retry hangs off this value, so anything
 * shorter (and 0 above all) reintroduces the dead air AUTO-4962 fixed.
 */
export const FLUX_USER_SILENCE_TIMEOUT_MIN = 10;

/**
 * Raises a stored or typed value to the Flux floor and drops any fraction.
 *
 * Same total guard as clampEotThreshold: `Number.isFinite` does not coerce, so
 * null/undefined, NaN, ±Infinity and anything non-numeric that slips past the
 * types fall back to the floor rather than reaching the API as 0.
 */
export const clampFluxUserSilenceTimeout = (
  value: number | null | undefined
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return FLUX_USER_SILENCE_TIMEOUT_MIN;
  }

  return Math.max(FLUX_USER_SILENCE_TIMEOUT_MIN, Math.trunc(value));
};

export const EOT_THRESHOLD_MIN = 0.5;
export const EOT_THRESHOLD_MAX = 0.9;
export const EOT_THRESHOLD_STEP = 0.1;
export const EOT_THRESHOLD_DEFAULT = 0.8;

/**
 * Snaps to the allowed 0.1 increments and clamps into [0.5, 0.9], so a stored
 * out-of-contract value (e.g. 0.55) can never reach the slider or the API.
 */
export const clampEotThreshold = (value: number | null | undefined): number => {
  // One total guard instead of a NaN check on a coerced value: `Number.isFinite`
  // does not coerce, so null/undefined, NaN, ±Infinity and anything non-numeric
  // that slips past the types all fall back to the default rather than silently
  // clamping to an edge of the range (`Number('')` would have yielded 0.5).
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EOT_THRESHOLD_DEFAULT;
  }

  const snapped = Math.round(value / EOT_THRESHOLD_STEP) * EOT_THRESHOLD_STEP;
  const clamped = Math.min(
    EOT_THRESHOLD_MAX,
    Math.max(EOT_THRESHOLD_MIN, snapped)
  );

  // toFixed guards against float drift (0.7000000000000001) so the persisted
  // value is always exactly one of the five allowed steps.
  return Number(clamped.toFixed(1));
};

type ConversationHandlingValues = {
  speechModel: string;
  inboundMessageBuffer: number | null;
  userSilenceTimeout: number | null;
  eotThreshold: number;
};

type ResolvedConversationHandling = {
  inboundMessageBufferMs: number | undefined;
  userSilenceTimeout: number | null;
  eotThreshold: number | undefined;
};

/**
 * Single source of truth for the three model-dependent Conversation Handling
 * values that go to the API. The form keeps the user's own values in state and
 * the Inbound Message Buffer zero is applied here, on the way out, so switching
 * back to a non-Flux model restores it with no bookkeeping.
 *
 * Max Caller Speaking Time is the exception: it is also floored HERE, because
 * this is the last gate before the API and a channel saved under the old rule
 * can still be carrying 0. The form's own seeding of that field on a model
 * switch is a separate, reversible concern — see
 * `resolveFluxMaxSpeakingTimeOnModelSwitch`, which does keep a record so that
 * switching away can undo it.
 */
export const resolveConversationHandling = ({
  speechModel,
  inboundMessageBuffer,
  userSilenceTimeout,
  eotThreshold,
}: ConversationHandlingValues): ResolvedConversationHandling => {
  if (isFluxModel(speechModel)) {
    // The buffer zero must still be sent — omitting it can break other models.
    // The timeout is the user's own value, floored: this is the last gate before
    // the API, so a stale 0 from a channel saved under the old rule is corrected
    // here even if the form never touched it.
    return {
      inboundMessageBufferMs: FLUX_LOCKED_VALUE,
      userSilenceTimeout: clampFluxUserSilenceTimeout(userSilenceTimeout),
      eotThreshold: clampEotThreshold(eotThreshold),
    };
  }

  return {
    inboundMessageBufferMs:
      inboundMessageBuffer == null ? undefined : Number(inboundMessageBuffer),
    userSilenceTimeout:
      userSilenceTimeout == null ? null : Number(userSilenceTimeout),
    // Does not apply outside Flux.
    eotThreshold: undefined,
  };
};

/** The two pieces of Max Caller Speaking Time UI state that move together. */
export type MaxSpeakingTimeState = {
  /** Seconds, or null when the setting is off (non-Flux only). */
  value: number | null;
  /** Whether the field is on. Always true under Flux, which has no toggle. */
  enabled: boolean;
};

type MaxSpeakingTimeOnLoad = MaxSpeakingTimeState & {
  /** The stored value was below the floor and has been raised — say so in the UI. */
  raised: boolean;
  /** The form differs from what is stored, so it must be dirty to be saveable. */
  changed: boolean;
};

/**
 * What Max Caller Speaking Time becomes when a channel is loaded.
 *
 * `changed` exists because the Save button is gated on the form being dirty: a
 * correction applied silently is one the user can see but never persist, so the
 * stored 0 — and the dead air — would survive (PSTUDIO-3941).
 */
export const resolveFluxMaxSpeakingTimeOnLoad = (
  stored: number | null | undefined,
  speechModel?: string | null
): MaxSpeakingTimeOnLoad => {
  const current = stored ?? null;

  if (!isFluxModel(speechModel)) {
    return {
      value: current,
      enabled: current != null,
      raised: false,
      changed: false,
    };
  }

  const floored = clampFluxUserSilenceTimeout(current);

  return {
    value: floored,
    // Flux always sends a value, so the field is on whatever was stored.
    enabled: true,
    // Only a value that existed can have been raised; an absent one was
    // defaulted, and calling that a raise misreports it to the user.
    raised: current != null && floored !== current,
    changed: floored !== current,
  };
};

/**
 * What Max Caller Speaking Time becomes when the speech model changes.
 *
 * Switching TO Flux seeds the floor when the current value cannot serve a retry
 * (AC 2: a channel "newly switched to Flux" defaults to 10s) and remembers what
 * it overwrote. Switching AWAY puts that back — without it, a non-Flux channel
 * with the setting off silently returns at 10s, because selecting the Deepgram
 * *provider* selects Flux by default, so a user can round-trip through Flux
 * without ever choosing it by name.
 */
export const resolveFluxMaxSpeakingTimeOnModelSwitch = ({
  newModel,
  current,
  captured,
}: {
  newModel: string;
  current: MaxSpeakingTimeState;
  captured: MaxSpeakingTimeState | null;
}): MaxSpeakingTimeState & { captured: MaxSpeakingTimeState | null } => {
  if (!isFluxModel(newModel)) {
    // Nothing was seeded, so there is nothing to undo.
    if (!captured) return { ...current, captured: null };
    return { ...captured, captured: null };
  }

  const floored = clampFluxUserSilenceTimeout(current.value);

  if (floored === current.value) {
    // Already legal for Flux — left untouched, so nothing needs remembering.
    return { value: floored, enabled: true, captured };
  }

  return {
    value: floored,
    enabled: true,
    captured: captured ?? current,
  };
};
